import moment from 'moment/moment'
import React, {FC, useCallback, useMemo, useState} from 'react'
import {useInView} from 'react-intersection-observer'
import {useDispatch} from 'react-redux'
import {useParams} from 'react-router'
import {
  ContentDisappearingPhotoType,
  ContentDisappearingVideoType,
  ContentPhotoType,
  ContentStickerType,
  ContentTextType,
  ContentVirtualGiftType,
  LoadingMediaType,
  MessageType,
  messageTypes,
  PhotoTypes,
  PurchaseMessageRequest,
  readDialog,
  ReadMessage,
  setPurchaseMessage,
  useOpenDialog,
  virtualGiftsOpenModal
} from 'entities/messages'
import {openGallery, setCurrentImage, setGalleryIsPhotoList} from 'entities/profile'
import {setVideoPlayer} from 'features/videoPlayer'
import {mediaVariants, PRICE} from 'shared/lib'
import {Button} from 'shared/ui/buttons'
import {GoCoin20, Play} from 'shared/ui/icons'
import {Loader} from 'shared/ui/loader'
import {Timer} from 'shared/ui/timer'
import styles from './styles.module.scss'

type MessageProps = {
  message: MessageType
  isMyMessage: boolean
}

let handler: number

export const Message: FC<MessageProps> = ({message, isMyMessage}) => {
  const dispatch = useDispatch()
  const openDialog = useOpenDialog()
  const {id: idUser} = useParams()
  const {ref} = useInView({onChange: (inView) => {
    if (!idUser || !inView || !openDialog) return

    if (openDialog?.idLastReadMessage == null || message.id > openDialog.idLastReadMessage) {
      handler && clearTimeout(handler)
      handler = window.setTimeout(() => {
        ReadMessage(idUser, message.id)
          .then(({data}) => {
            if (!data) return

            dispatch(readDialog({idUser, newIdLastReadMessage: message.id, unreadCount: data.unreadCount}))
          })
          .catch(console.error)
      }, 500)
    }
  }, skip: isMyMessage})

  const time = useMemo(
    () => message.dateCreated ? moment(message.dateCreated).format('HH:mm') : '',
    [message.dateCreated]
  )

  const content = useCallback(() => {
    switch (message.type) {
    case messageTypes.text:
      return <MessageText
        time={time}
        text={(message.content as ContentTextType).text}
        isMyMessage={isMyMessage}
      />
    case messageTypes.photo:
      return <MessagePhoto
        url={(message.content as ContentPhotoType).photo.url}
        time={time}
      />
    case messageTypes.disappearingPhoto:
      return <MessageDisappearingPhoto
        content={message.content as ContentDisappearingPhotoType}
        time={time}
        isMyMessage={isMyMessage}
        idMessage={message.id}
      />
    case messageTypes.video:
    case messageTypes.disappearingVideo:
      return <MessageVideoAndDisappearingVideo
        content={message.content as ContentDisappearingVideoType}
        time={time}
        isMyMessage={isMyMessage}
        idMessage={message.id}
      />
    case messageTypes.sticker:
      return <MessageSticker
        time={time}
        url={(message.content as ContentStickerType).sticker.data.url}
      />
    case messageTypes.virtualGift:
      return <MessageVirtualGift
        time={time}
        content={message.content as ContentVirtualGiftType}
        isMyMessage={isMyMessage}
      />
    default: return <></>
    }
  }, [message, isMyMessage])

  return (
    <div ref={ref} className={styles.messageWrapper}>
      {content()}
    </div>
  )
}

type MessageTextProps = {
  time?: string
  text: string
  isMyMessage: boolean
}

export const MessageText: FC<MessageTextProps> = ({time, text, isMyMessage}) => {
  return (
    <div className={`
      ${styles.message}
      ${isMyMessage ? styles.rightBackground : styles.leftBackground}
      ${isMyMessage ? styles.rightText : styles.leftText}
    `}>
      <div className={styles.text}>{text}</div>
      {time ? <div className={styles.time}>{time}</div> : <div className={styles.loader}><Loader size={16} /></div>}
    </div>
  )
}

type MessagePhotoProps = {
  url: string
  time: string
}

const MessagePhoto: FC<MessagePhotoProps> = ({url, time}) => {
  const dispatch = useDispatch()

  const openPhoto = () => {
    dispatch(openGallery())
    dispatch(setGalleryIsPhotoList(false))
    dispatch(setCurrentImage({
      index: 0,
      type: PhotoTypes.attachment,
      src: url
    }))
  }

  return (
    <div className={styles.media} onClick={openPhoto}>
      <img src={url + mediaVariants.originalM} className={styles.img} alt='photo' />
      <div className={styles.time}>{time}</div>
    </div>
  )
}

type MessageDisappearingPhotoProps = {
  content: ContentDisappearingPhotoType
  time: string
  isMyMessage: boolean
  idMessage: string
}

const MessageDisappearingPhoto: FC<MessageDisappearingPhotoProps> = ({
  content,
  time,
  isMyMessage,
  idMessage
}) => {
  const dispatch = useDispatch()
  const [isLoad, setIsLoad] = useState(false)

  const openPhoto = () => {
    if (!content.photo) return

    dispatch(openGallery())
    dispatch(setGalleryIsPhotoList(false))
    dispatch(setCurrentImage({
      index: 0,
      type: PhotoTypes.attachment,
      src: content.photo.url
    }))
  }

  const purchasePhoto = () => {
    if (isLoad) return

    setIsLoad(true)
    PurchaseMessageRequest(idMessage)
      .then(({data}) => dispatch(setPurchaseMessage(data)))
      .catch(console.error)
      .finally(() => setIsLoad(false))
  }

  if (!content?.photo) {
    return (
      <div data-id={idMessage} className={`${styles.timeOut} ${isMyMessage ? styles.rightBackground : styles.leftBackground}`}>
        <div className={styles.header}>Time out</div>
        <div className={styles.text}>The time to view this media is over</div>
        <div className={styles.time}>{time}</div>
      </div>
    )
  }

  if (content.purchased === false) {
    return (
      <div className={styles.unpaidPhoto}>
        <img src={content.photo.url + mediaVariants.square512blur} className={styles.img} alt='photo' />
        <div className={styles.background} />
        <div className={styles.info}>
          <div className={styles.timer}>
            <Timer seconds={moment(content.dateExpired).diff(new Date(), 'seconds')} />
          </div>
          <div className={styles.header}>Disappearing message</div>
          <div>Once purchased, the media file will be available for 12 hours</div>
          <div className={`${styles.openMediaButton} ${styles.openPhoto}`} onClick={purchasePhoto}>
            {!isLoad ? <>
              Open now for {PRICE.CHAT_DISAPPEARING_PHOTO_VIEW}
              <GoCoin20 />
            </> : <Loader className={styles.load} />}
          </div>
        </div>
        <div className={styles.time}>{time}</div>
      </div>
    )
  }

  return (
    <div className={styles.media} onClick={openPhoto}>
      <div className={styles.timer}>
        <Timer seconds={moment(content.dateExpired).diff(new Date(), 'seconds')} />
      </div>
      <img src={content.photo.url + mediaVariants.originalM} className={styles.img} alt='photo' />
      <div className={styles.time}>{time}</div>
    </div>
  )
}

type MessageVideoAndDisappearingVideoProps = {
  content: ContentDisappearingVideoType
  time: string
  idMessage: string
  isMyMessage: boolean
}

const MessageVideoAndDisappearingVideo: FC<MessageVideoAndDisappearingVideoProps> = ({
  content,
  time,
  isMyMessage,
  idMessage
}) => {
  const dispatch = useDispatch()
  const [isLoad, setIsLoad] = useState(false)

  const openVideoPlayer = () => dispatch(setVideoPlayer({
    iframe: content.video?.iframeUrl,
    poster: content.video?.jpgUrl
  }))

  const purchaseVideo = () => {
    if (isLoad) return

    setIsLoad(true)
    PurchaseMessageRequest(idMessage)
      .then(({data}) => {
        openVideoPlayer()
        dispatch(setPurchaseMessage(data))
      })
      .catch(console.error)
      .finally(() => setIsLoad(false))
  }

  if (!content?.video) {
    return (
      <div className={`${styles.timeOut} ${isMyMessage ? styles.rightBackground : styles.leftBackground}`}>
        <div className={styles.header}>Time out</div>
        <div className={styles.text}>The time to view this media is over</div>
        <div className={styles.time}>{time}</div>
      </div>
    )
  }

  if (content.purchased === false) {
    return (
      <div className={styles.unpaidVideo}>
        <img src={content.video.jpgUrl} className={`${styles.img} ${content.dateExpired ? styles.blur : ''}`} alt='photo' />
        <div className={styles.background} />
        <div className={styles.info}>
          {content.dateExpired ? <>
            <div className={styles.timer}>
              <Timer seconds={moment(content.dateExpired).diff(new Date(), 'seconds')} />
            </div>
            <div className={styles.header}>Disappearing message</div>
            <div>Once purchased, the media file will be available for 12 hours</div>
          </> : null}
          <div className={`${styles.openMediaButton} ${styles.openVideo}`} onClick={purchaseVideo}>
            {!isLoad ? <>
              <div className={styles.playIcon}>
                <Play />
              </div>
              Watch now for {content.dateExpired ? PRICE.CHAT_DISAPPEARING_VIDEO_VIEW : PRICE.CHAT_VIDEO_VIEW}
              <GoCoin20 />
            </> : <Loader className={styles.load} />}
          </div>
        </div>
        <div className={styles.time}>{time}</div>
      </div>
    )
  }

  return (
    <div className={styles.media}>
      {content.dateExpired ? <div className={styles.timer}>
        <Timer seconds={moment(content.dateExpired).diff(new Date(), 'seconds')} />
      </div> : null}
      <img src={content.video.jpgUrl} className={styles.img} alt='video' />
      <div className={styles.time}>{time}</div>
      <div className={styles.playIcon} onClick={openVideoPlayer}>
        <Play />
      </div>
    </div>
  )
}

type MessageStickerProps = {
  time: string
  url: string
}

const MessageSticker: FC<MessageStickerProps> = ({time, url}) => {
  return (
    <div className={styles.sticker}>
      <div className={styles.imgWrapper}>
        <img src={url + mediaVariants.square512} className={styles.img} alt='sticker' />
      </div>
      <div className={styles.timeWrapper}>
        <div className={styles.time}>{time}</div>
      </div>
    </div>
  )
}

type MessageVirtualGiftProps = {
  time: string
  content: ContentVirtualGiftType
  isMyMessage: boolean
}

const MessageVirtualGift: FC<MessageVirtualGiftProps> = ({
  time,
  content,
  isMyMessage
}) => {
  const dispatch = useDispatch()
  const openVirtualGiftModal = useCallback(() => dispatch(virtualGiftsOpenModal()), [dispatch, virtualGiftsOpenModal])

  return (
    <div className={`${styles.virtualGift} ${isMyMessage ? styles.rightBackground : styles.leftBackground}`}>
      <div className={`${styles.virtualGiftWrapper} ${styles[`backgroundGradient${content.gradient}`]}`}>
        <img
          src={content.virtualGift.data.url + mediaVariants.square512} alt='gift'
          className={styles.gift}
        />
        <Button styleType='primary' fullwidth onClick={openVirtualGiftModal}>
          {isMyMessage ? 'Send more gift' : 'Send a gift in return'}
        </Button>
      </div>
      {content.text ? <div className={styles.text}>{content.text}</div> : null}
      <div className={styles.time}>{time}</div>
    </div>
  )
}

export const MessageLoadingMedia: FC<{media: LoadingMediaType}> = ({media}) => {
  return (
    <div className={styles.media} key={media.cfId}>
      {media.type === 'photo'
        ? <img src={media.previewUrl} alt='loadMedia' className={styles.img} />
        : <video src={media.previewUrl} className={styles.img} />}
      <div className={styles.backLoad} />
      <Loader className={styles.loadMedia} innerColor='white' />
    </div>
  )
}